import React, { useState, useEffect } from "react";
import { api } from "../../lib/apiClient";
import { Save } from "lucide-react";

export function CostSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/cost-settings").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="p-6 text-slate-400 text-sm">Loading…</div>;

  const update = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const rate = Number(cfg.labour_cost_rate);
      if (!Number.isFinite(rate) || rate < 0) throw new Error("Labour cost rate must be a non-negative number.");
      await api.put("/cost-settings", {
        labour_cost_rate: rate,
        cost_label: (cfg.cost_label || "").trim() || "Cost",
        sell_label: (cfg.sell_label || "").trim() || "Sell price to client",
        complex_label: (cfg.complex_label || "").trim() || "Complex",
        complex_default_hours: Math.max(0, Number(cfg.complex_default_hours ?? 4)),
        max_working_day_hours: Math.max(0.5, Number(cfg.max_working_day_hours ?? 12)),
        hotel_engineer_bonus_per_night: Math.max(0, Number(cfg.hotel_engineer_bonus_per_night ?? 130)),
        hotel_food_per_engineer_per_night: Math.max(0, Number(cfg.hotel_food_per_engineer_per_night ?? 30)),
        hotel_contingency_total: Math.max(0, Number(cfg.hotel_contingency_total ?? 50)),
        hotel_trigger_one_way_hours: Math.max(0, Number(cfg.hotel_trigger_one_way_hours ?? 2)),
      });
      setInfo("Saved. The Quote Builder live calculator will pick this up on the next preview.");
      load();
    } catch (ex) {
      setErr(ex.response?.data?.detail?.toString() || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="cost-settings-tab">
      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Internal cost rate</div>
        <p className="text-sm text-slate-600">Used to compute the &ldquo;{cfg.cost_label || "Cost"}&rdquo; total in the live calculator. Each labour row&rsquo;s hours × the row&rsquo;s multiplier × this rate gives our internal labour cost. Materials and Access Hire contribute their supplier cost (before markup).</p>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Labour cost rate (£/hour)</label>
          <div className="relative max-w-[200px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none z-10">£</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={cfg.labour_cost_rate ?? 45}
              onChange={(e) => update({ labour_cost_rate: e.target.value })}
              className="qb-input font-mono no-spinner"
              style={{ paddingLeft: 28, paddingRight: 12 }}
              data-testid="labour-cost-rate-input"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Default £45.00 — scales automatically with each labour row&rsquo;s multiplier (×1, ×1.5, ×2).</div>
        </div>
      </div>

      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total labels (live calculator)</div>
        <p className="text-sm text-slate-600">Wording for the two totals shown at the bottom of the Quote Builder.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Cost label (top row)</label>
            <input value={cfg.cost_label || ""} onChange={(e) => update({ cost_label: e.target.value })} className="qb-input" placeholder="Cost" data-testid="cost-label-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Sell label (highlighted row)</label>
            <input value={cfg.sell_label || ""} onChange={(e) => update({ sell_label: e.target.value })} className="qb-input" placeholder="Sell price to client" data-testid="sell-label-input" />
          </div>
        </div>
      </div>

      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Working day length</div>
        <p className="text-sm text-slate-600">The Quote Builder watches the running total of <strong>labour + travel hours</strong> as the estimator builds a quote. The moment it exceeds this threshold the user is asked whether the job spans multiple days. If they say yes, the travel labour row is multiplied by the number of days entered (so the team is correctly billed for one round-trip <em>per</em> day).</p>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Maximum working day (hours)</label>
          <div className="relative max-w-[200px]">
            <input
              type="number"
              step="0.5"
              min="0.5"
              value={cfg.max_working_day_hours ?? 12}
              onChange={(e) => update({ max_working_day_hours: e.target.value })}
              className="qb-input font-mono no-spinner"
              style={{ paddingLeft: 12, paddingRight: 12 }}
              data-testid="max-working-day-input"
            />
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Default 12 hours. The prompt fires once when the total first crosses this number — the user can confirm a 1-day job (no change) or enter the number of days the job spans.</div>
        </div>
      </div>

      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Difficulty: Complex</div>
        <p className="text-sm text-slate-600">When a labour row is set to &ldquo;Complex&rdquo;, this many additional hours are added to that row. Each product in the catalogue can override this with its own &ldquo;Complex hours&rdquo; figure.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Label shown on labour rows</label>
            <input value={cfg.complex_label || ""} onChange={(e) => update({ complex_label: e.target.value })} className="qb-input" placeholder="Complex" data-testid="complex-label-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Default extra hours added</label>
            <div className="relative max-w-[160px]">
              <input
                type="number" step="0.25" min="0"
                value={cfg.complex_default_hours ?? 4}
                onChange={(e) => update({ complex_default_hours: e.target.value })}
                className="qb-input font-mono"
                data-testid="complex-default-hours-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="qb-card p-5 space-y-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Hotel rules</div>
        <p className="text-sm text-slate-600">
          Used by the Hotel section in the Quote Builder. The bonus + food figures scale by <strong>engineers × total nights</strong>; the contingency is added <strong>per room</strong> (£50 × total rooms across all hotel rows). The trigger threshold opens a prompt asking the estimator if a hotel is needed when the one-way travel time crosses it.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Engineer bonus (£ / engineer / night)</label>
            <input type="number" step="1" min="0" value={cfg.hotel_engineer_bonus_per_night ?? 130} onChange={(e) => update({ hotel_engineer_bonus_per_night: e.target.value })} className="qb-input font-mono max-w-[160px]" data-testid="hotel-bonus-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Food (£ / engineer / night)</label>
            <input type="number" step="1" min="0" value={cfg.hotel_food_per_engineer_per_night ?? 30} onChange={(e) => update({ hotel_food_per_engineer_per_night: e.target.value })} className="qb-input font-mono max-w-[160px]" data-testid="hotel-food-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Hotel contingency (£ per room)</label>
            <input type="number" step="1" min="0" value={cfg.hotel_contingency_total ?? 50} onChange={(e) => update({ hotel_contingency_total: e.target.value })} className="qb-input font-mono max-w-[160px]" data-testid="hotel-contingency-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block font-semibold">Prompt at one-way travel ≥ (hours)</label>
            <input type="number" step="0.25" min="0" value={cfg.hotel_trigger_one_way_hours ?? 2} onChange={(e) => update({ hotel_trigger_one_way_hours: e.target.value })} className="qb-input font-mono max-w-[160px]" data-testid="hotel-trigger-input" />
          </div>
        </div>
      </div>


      {info && <div className="border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm px-3 py-2 rounded-lg" data-testid="cost-settings-info">{info}</div>}
      {err && <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="cost-settings-error">{err}</div>}

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark disabled:opacity-60 rounded-full" data-testid="cost-settings-save">
          <Save size={14} /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
